import { ChevronDown } from "lucide-react";
import { Link } from "react-router-dom";
import { useState, useEffect } from "react";
import LocationPickerModal from "./location-picker-modal";
import { retrieveMasterCity } from "../../services/locationServices";

export default function Navbar() {
  const [isLocationModalOpen, setIsLocationModalOpen] = useState(false);
  const [selectedLocation, setSelectedLocation] = useState("Detecting location...");
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isDetectingLocation, setIsDetectingLocation] = useState(true);

  useEffect(() => {
    const detectLocation = async () => {
      if (!navigator.geolocation) {
        setSelectedLocation("Austin, Texas");
        localStorage.setItem("geohash", "9v6m");
        setIsDetectingLocation(false);
        return;
      }

      try {
        const position = await new Promise((resolve, reject) => {
          navigator.geolocation.getCurrentPosition(
            resolve,
            (error) => {
              if (error.code === 1) {
                reject({ type: 'permission_denied', error });
              } else {
                reject({ type: 'other', error });
              }
            },
            {
              enableHighAccuracy: true,
              timeout: 10000,
              maximumAge: 0
            }
          );
        });

        const { latitude, longitude } = position.coords;
        
        const payload = {
          latitude: latitude,
          longitude: longitude,
          state: "",
          city: ""
        };

        const cityResponse = await retrieveMasterCity(payload);
        
        let locationString = "Austin, Texas";
        let geohashToUse = "9v6m";
        
        // API returns { status, geohash: { geohash, lat, long, city, state } }
        if (cityResponse?.geohash && typeof cityResponse.geohash === "object") {
          const cityData = cityResponse.geohash;
          const city = cityData.city || "Unknown";
          const state = cityData.state || "";
          locationString = state ? `${city}, ${state}` : city;
          const apiGeohash = cityData.geohash;
          if (apiGeohash && typeof apiGeohash === "string" && apiGeohash.trim() !== "" && apiGeohash !== "null") {
            geohashToUse = apiGeohash.trim();
          }
        } else {
          try {
            const reverseGeocodeResponse = await fetch(
              `https://nominatim.openstreetmap.org/reverse?format=json&lat=${latitude}&lon=${longitude}&zoom=18&addressdetails=1`,
              {
                headers: {
                  "Accept-Language": "en-US,en;q=0.9",
                  "User-Agent": "GallyoLocationDetector"
                }
              }
            );
            
            if (reverseGeocodeResponse.ok) {
              const reverseData = await reverseGeocodeResponse.json();
              const city = reverseData.address?.city || 
                          reverseData.address?.town || 
                          reverseData.address?.village || 
                          reverseData.address?.county || 
                          "Unknown";
              const state = reverseData.address?.state || 
                           reverseData.address?.region || 
                           "";
              locationString = state ? `${city}, ${state}` : city;
            }
          } catch (reverseError) {
            console.error("Reverse geocoding error:", reverseError);
          }
        }
        
        setSelectedLocation(locationString);
        localStorage.setItem("geohash", geohashToUse);
        window.dispatchEvent(new CustomEvent("geohashChanged", { 
          detail: { geohash: geohashToUse } 
        }));
      } catch (error) {
        setSelectedLocation("Austin, Texas");
        const defaultGeohash = "9v6m";
        localStorage.setItem("geohash", defaultGeohash);
        window.dispatchEvent(new CustomEvent("geohashChanged", { 
          detail: { geohash: defaultGeohash } 
        }));
      } finally {
        setIsDetectingLocation(false);
      }
    };

    detectLocation();
  }, []);

  const handleLocationSelect = (location) => {
    if (location.name) {
      const locationString = location.state 
        ? `${location.name}, ${location.state}`
        : location.name;
      
      setSelectedLocation(locationString);
      
      const geohash = location.geohash || "9v6m";
      localStorage.setItem("geohash", geohash);
      window.dispatchEvent(new CustomEvent("geohashChanged", { 
        detail: { geohash } 
      }));
    }
    setIsLocationModalOpen(false);
  };
  
  const displayLocation = selectedLocation.includes(",")
    ? selectedLocation.split(",")[0] 
    : selectedLocation;
  const shortLocation = isDetectingLocation 
    ? "Detecting..." 
    : displayLocation.split(" ").slice(0, 2).join(" ");

  return (
    <div className="w-full shadow-md">
      <LocationPickerModal
        isOpen={isLocationModalOpen}
        onClose={() => setIsLocationModalOpen(false)}
        onSelectLocation={handleLocationSelect}
      />

      <div className="flex items-center justify-between px-4 sm:px-6 md:px-8 lg:px-12 xl:px-16 2xl:px-20 3xl:px-24 py-3 sm:py-4 md:py-4 lg:py-5 bg-white">
        <style>{`
          @import url('https://fonts.googleapis.com/css2?family=Afacad:wght@400;500;600&display=swap');
          .afacad-font {
            font-family: 'Afacad', sans-serif;
          }
        `}</style>
        <div className="flex items-center gap-3 sm:gap-4 md:gap-4 lg:gap-5 xl:gap-6">
          <img
            src="/assets/gallyo-logo.png"
            alt="Gallyo"
            width={126}
            height={53}
            className="h-[33px] sm:h-[40px] md:h-[45px] lg:h-[50px] xl:h-[53px] w-auto"
            style={{ width: 'auto', height: 'clamp(33px, 2.75vw, 53px)' }}
          />
          <button
            onClick={() => setIsLocationModalOpen(true)}
            className="flex items-center gap-1 text-gray-700 hover:text-[#7B189F] transition-colors cursor-pointer afacad-font disabled:opacity-50 disabled:cursor-not-allowed"
            style={{ fontWeight: 500, fontSize: 'clamp(12px, 0.83vw, 16px)' }}
            disabled={isDetectingLocation}
          >
            <span className="hidden sm:inline">
              {isDetectingLocation ? "Detecting location..." : selectedLocation}
            </span>
            <span className="sm:hidden">
              {isDetectingLocation ? "Detecting..." : shortLocation}
            </span>
            <ChevronDown className="w-3 h-3 sm:w-4 sm:h-4" />
          </button>
        </div>

        <div className="flex items-center gap-2 sm:gap-3 md:gap-4 lg:gap-5 xl:gap-6">
          <button className="afacad-font text-gray-700 font-normal hover:text-[#7B189F] hidden lg:block px-2 xl:px-3" style={{ fontSize: 'clamp(12px, 0.83vw, 16px)' }}>
            Advertise
          </button>
          <Link to="/classifieds">
            <button className="afacad-font bg-[#7B189F] text-white px-3 sm:px-4 md:px-5 lg:px-6 xl:px-8 py-1.5 sm:py-2 md:py-2.5 lg:py-3 rounded-lg font-normal hover:bg-[#6a1489] hidden lg:block" style={{ fontSize: 'clamp(12px, 0.83vw, 16px)' }}>
              Post Classified
            </button>
          </Link>

          <div className="hidden lg:block" style={{ 
            width: '1px',
            height: 'clamp(20px, 1.39vw, 24px)',
            backgroundColor: '#D1D5DB',
            margin: '0 clamp(4px, 0.35vw, 8px)'
          }} />

          <Link to="/signin">
            <button className="afacad-font bg-[#7B189F] text-white px-3 sm:px-4 md:px-5 lg:px-6 xl:px-8 py-1.5 sm:py-2 md:py-2.5 lg:py-3 rounded-lg font-normal hover:bg-[#6a1489] hidden lg:block" style={{ fontSize: 'clamp(12px, 0.83vw, 16px)' }}>
              Login
            </button>
          </Link>
          <div className="w-8 h-8 sm:w-9 sm:h-9 md:w-10 md:h-10 rounded-full overflow-hidden lg:hidden">
            <button 
              onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
              className="w-full h-full flex items-center justify-center"
            >
              {isMobileMenuOpen ? (
                <svg
                  className="w-5 h-5 sm:w-6 sm:h-6"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M6 18L18 6M6 6l12 12"
                  />
                </svg>
              ) : (
                <svg
                  className="w-5 h-5 sm:w-6 sm:h-6"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M4 6h16M4 12h16M4 18h16"
                  />
                </svg>
              )}
            </button>
          </div>
        </div>
      </div>

      <div className="bg-[#7B189F] text-white text-center hidden md:block" style={{ padding: 'clamp(4px, 0.35vw, 6px) 0' }}>
        <div className="flex items-center justify-center gap-2 px-4">
          <span className="text-yellow-300" style={{ fontSize: 'clamp(12px, 1.04vw, 16px)' }}>🎉</span>
          <span
            className="font-medium"
            style={{ 
              fontFamily: "Afacad, sans-serif",
              fontSize: 'clamp(10px, 0.87vw, 14px)'
            }}
          >
            CHECKOUT OUR NEW CONFERENCE ON AI AND LLMS
          </span>
        </div>
      </div>

      {isMobileMenuOpen && (
        <div className="lg:hidden bg-white border-b border-gray-200 shadow-lg max-h-[80vh] overflow-y-auto">
          <div className="px-4 py-4 space-y-4">
            <div className="space-y-2">
              <div className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-3 px-2">
                Navigation
              </div>
              <Link to="/scoops" onClick={() => setIsMobileMenuOpen(false)} className="flex items-center gap-3 text-gray-700 hover:text-[#7B189F] transition-colors py-2 px-2 rounded-lg hover:bg-gray-50">
                <img
                  src="/landing/nav/scoop.png"
                  alt="Scoops"
                  className="w-6 h-6"
                />
                <span className="afacad-font font-medium" style={{ fontSize: 'clamp(14px, 1.04vw, 16px)' }}>Scoops</span>
              </Link>
              <Link to="/makeyaari-onboarding" onClick={() => setIsMobileMenuOpen(false)} className="flex items-center gap-3 text-gray-700 hover:text-[#7B189F] transition-colors py-2 px-2 rounded-lg hover:bg-gray-50">
                <img
                  src="/landing/nav/yaari.png"
                  alt="Yaari"
                  className="w-6 h-6"
                />
                <span className="afacad-font font-medium" style={{ fontSize: 'clamp(14px, 1.04vw, 16px)' }}>Yaari</span>
              </Link>
              <Link to="/events" onClick={() => setIsMobileMenuOpen(false)} className="flex items-center gap-3 text-gray-700 hover:text-[#7B189F] transition-colors py-2 px-2 rounded-lg hover:bg-gray-50">
                <img
                  src="/landing/nav/event.png"
                  alt="Events"
                  className="w-6 h-6"
                />
                <span className="afacad-font font-medium" style={{ fontSize: 'clamp(14px, 1.04vw, 16px)' }}>Events</span>
              </Link>
              <Link to="/discover" onClick={() => setIsMobileMenuOpen(false)} className="flex items-center gap-3 text-gray-700 hover:text-[#7B189F] transition-colors py-2 px-2 rounded-lg hover:bg-gray-50">
                <img
                  src="/landing/nav/discover.png"
                  alt="Discover"
                  className="w-6 h-6"
                />
                <span className="afacad-font font-medium" style={{ fontSize: 'clamp(14px, 1.04vw, 16px)' }}>Discover</span>
              </Link>
              <Link to="/groups" onClick={() => setIsMobileMenuOpen(false)} className="flex items-center gap-3 text-gray-700 hover:text-[#7B189F] transition-colors py-2 px-2 rounded-lg hover:bg-gray-50">
                <img
                  src="/landing/nav/roommates.png"
                  alt="Groups"
                  className="w-6 h-6"
                />
                <span className="afacad-font font-medium" style={{ fontSize: 'clamp(14px, 1.04vw, 16px)' }}>Groups</span>
              </Link>
              <Link to="/classifieds?category=Roommate" onClick={() => setIsMobileMenuOpen(false)} className="flex items-center gap-3 text-gray-700 hover:text-[#7B189F] transition-colors py-2 px-2 rounded-lg hover:bg-gray-50">
                <img
                  src="/landing/nav/roommates.png"
                  alt="Roomates"
                  className="w-6 h-6"
                />
                <span className="afacad-font font-medium" style={{ fontSize: 'clamp(14px, 1.04vw, 16px)' }}>Roomates</span>
              </Link>
              <Link to="/more" onClick={() => setIsMobileMenuOpen(false)} className="flex items-center gap-3 text-gray-700 hover:text-[#7B189F] transition-colors py-2 px-2 rounded-lg hover:bg-gray-50">
                <img
                  src="/landing/nav/carrer.png"
                  alt="Career Guidance"
                  className="w-6 h-6"
                />
                <span className="afacad-font font-medium" style={{ fontSize: 'clamp(14px, 1.04vw, 16px)' }}>Career Guidance</span>
              </Link>
            </div>

            <div className="h-px bg-gray-200 my-2"></div>

            <div className="space-y-2">
              <div className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-3 px-2">
                Actions
              </div>
              <button 
                className="afacad-font text-gray-700 font-normal hover:text-[#7B189F] w-full text-left py-2 px-2 rounded-lg hover:bg-gray-50 transition-colors" 
                style={{ fontSize: 'clamp(14px, 1.04vw, 16px)' }}
              >
                Advertise
              </button>
              <Link to="/classifieds" onClick={() => setIsMobileMenuOpen(false)} className="block">
                <button className="afacad-font bg-[#7B189F] text-white w-full py-3 rounded-lg font-medium hover:bg-[#6a1489] text-center transition-colors" style={{ fontSize: 'clamp(14px, 1.04vw, 16px)' }}>
                  Post Classified
                </button>
              </Link>
              <div className="h-px bg-gray-200 my-2"></div>
              <Link to="/signin" onClick={() => setIsMobileMenuOpen(false)} className="block">
                <button className="afacad-font bg-[#7B189F] text-white w-full py-3 rounded-lg font-medium hover:bg-[#6a1489] text-center transition-colors" style={{ fontSize: 'clamp(14px, 1.04vw, 16px)' }}>
                  Login
                </button>
              </Link>
            </div>
          </div>
        </div>
      )}

      <div className="bg-white border-b border-gray-200 px-4 sm:px-6 md:px-8 lg:px-12 xl:px-16 2xl:px-20 3xl:px-24 hidden md:block" style={{ minHeight: '36px', height: 'auto' }}>
        <div className="flex items-center justify-center gap-4 sm:gap-6 md:gap-8 lg:gap-10 xl:gap-12 2xl:gap-16 3xl:gap-20 flex-wrap" style={{ padding: '8px 0' }}>
          <Link to="/scoops" className="flex items-center gap-2 text-gray-700 hover:text-[#7B189F] cursor-pointer transition-colors afacad-font" style={{ fontWeight: 500, fontSize: 'clamp(10px, 0.73vw, 14px)' }}>
            <img
              src="/landing/nav/scoop.png"
              alt="Scoops"
              className="w-[25px] h-[25px] sm:w-[30px] sm:h-[30px] md:w-[35px] md:h-[35px] lg:w-[40px] lg:h-[40px]"
              style={{ width: 'clamp(25px, 2.08vw, 40px)', height: 'clamp(25px, 2.08vw, 40px)' }}
            />
            <span>Scoops</span>
          </Link>
          <Link to="/makeyaari-onboarding" className="flex items-center gap-2 text-gray-700 hover:text-[#7B189F] cursor-pointer transition-colors afacad-font" style={{ fontWeight: 500, fontSize: 'clamp(10px, 0.73vw, 14px)' }}>
            <img
              src="/landing/nav/yaari.png"
              alt="Yaari"
              className="w-[25px] h-[25px] sm:w-[30px] sm:h-[30px] md:w-[35px] md:h-[35px] lg:w-[40px] lg:h-[40px]"
              style={{ width: 'clamp(25px, 2.08vw, 40px)', height: 'clamp(25px, 2.08vw, 40px)' }}
            />
            <span>Yaari</span>
          </Link>
          <Link to="/events" className="flex items-center gap-2 text-gray-700 hover:text-[#7B189F] cursor-pointer transition-colors afacad-font" style={{ fontWeight: 500, fontSize: 'clamp(10px, 0.73vw, 14px)' }}>
            <img
              src="/landing/nav/event.png"
              alt="Events"
              className="w-[25px] h-[25px] sm:w-[30px] sm:h-[30px] md:w-[35px] md:h-[35px] lg:w-[40px] lg:h-[40px]"
              style={{ width: 'clamp(25px, 2.08vw, 40px)', height: 'clamp(25px, 2.08vw, 40px)' }}
            />
            <span>Events</span>
          </Link>
          <Link to="/discover" className="flex items-center gap-2 text-gray-700 hover:text-[#7B189F] cursor-pointer transition-colors afacad-font" style={{ fontWeight: 500, fontSize: 'clamp(10px, 0.73vw, 14px)' }}>
            <img
              src="/landing/nav/discover.png"
              alt="Discover"
              className="w-[25px] h-[25px] sm:w-[30px] sm:h-[30px] md:w-[35px] md:h-[35px] lg:w-[40px] lg:h-[40px]"
              style={{ width: 'clamp(25px, 2.08vw, 40px)', height: 'clamp(25px, 2.08vw, 40px)' }}
            />
            <span>Discover</span>
          </Link>
          <Link to="/classifieds?category=Roommate" className="flex items-center gap-2 text-gray-700 hover:text-[#7B189F] cursor-pointer transition-colors afacad-font" style={{ fontWeight: 500, fontSize: 'clamp(10px, 0.73vw, 14px)' }}>
            <img
              src="/landing/nav/roommates.png"
              alt="Roomates"
              className="w-[25px] h-[25px] sm:w-[30px] sm:h-[30px] md:w-[35px] md:h-[35px] lg:w-[40px] lg:h-[40px]"
              style={{ width: 'clamp(25px, 2.08vw, 40px)', height: 'clamp(25px, 2.08vw, 40px)' }}
            />
            <span>Roomates</span>
          </Link>
          <Link to="/more" className="flex items-center gap-2 text-gray-700 hover:text-[#7B189F] cursor-pointer transition-colors afacad-font" style={{ fontWeight: 500, fontSize: 'clamp(10px, 0.73vw, 14px)' }}>
            <img
              src="/landing/nav/carrer.png"
              alt="Career Guidance"
              className="w-[25px] h-[25px] sm:w-[30px] sm:h-[30px] md:w-[35px] md:h-[35px] lg:w-[40px] lg:h-[40px]"
              style={{ width: 'clamp(25px, 2.08vw, 40px)', height: 'clamp(25px, 2.08vw, 40px)' }}
            />
            <span>Career Guidance</span>
          </Link>
        </div>
      </div>
    </div>
  );
}
